import { useEffect, useState } from "react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  getImageDimensions,
  isSixteenNineConforming,
  THUMBNAIL_CONSTRAINT_TEXT,
} from "@/utils/imageAspectRatio";

interface ThumbnailConstraintWarningProps {
  /** The publicUrl of the currently-selected thumbnail, if any. */
  url?: string | null;
}

/**
 * Renders a prominent warning directly beneath a thumbnail picker when the
 * selected image is not exactly 16:9 / >=1280x720. Non-blocking — it surfaces
 * the problem; saving is still allowed (offenders are also flagged by the admin
 * banner and fixable on the remediation page).
 */
export default function ThumbnailConstraintWarning({
  url,
}: ThumbnailConstraintWarningProps) {
  const [isViolation, setIsViolation] = useState(false);

  useEffect(() => {
    let cancelled = false;

    if (!url) {
      setIsViolation(false);
      return;
    }

    getImageDimensions(url)
      .then(({ width, height }) => {
        if (!cancelled) {
          setIsViolation(!isSixteenNineConforming(width, height));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setIsViolation(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  if (!isViolation) {
    return null;
  }

  return (
    <Alert className="mt-2" variant="destructive">
      <AlertDescription>{THUMBNAIL_CONSTRAINT_TEXT}</AlertDescription>
    </Alert>
  );
}
